import { TextField } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { EntityFormDialog } from '../../components/dialogs/EntityFormDialog';
import { useCreateSupplier, useEditSupplier } from '../../api/queries';
import type { SupplierSummary } from '../../api/types';

const schema = z.object({
  code: z.string()
    .trim()
    .min(2, 'At least 2 characters')
    .max(40, 'At most 40 characters')
    .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, 'Letters, digits, _ and - only; must start with a letter or digit'),
  displayName: z.string().trim().min(1, 'Required').max(120),
  description: z.string().trim().max(500).optional()
                .or(z.literal('').transform(() => undefined)),
  primaryContactEmail: z.string().trim().email('Must be a valid email').max(200),
});

export type SupplierFormValues = z.infer<typeof schema>;

export type SupplierFormDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  supplier?: SupplierSummary;
  onClose: () => void;
};

export function SupplierFormDialog({ open, mode, supplier, onClose }: SupplierFormDialogProps) {
  const create = useCreateSupplier();
  const edit = useEditSupplier(supplier?.id ?? '');

  const initial: SupplierFormValues = mode === 'edit' && supplier
    ? {
        code:                supplier.code,
        displayName:         supplier.displayName,
        description:         supplier.description ?? '',
        primaryContactEmail: supplier.primaryContactEmail,
      }
    : {
        code:                '',
        displayName:         '',
        description:         '',
        primaryContactEmail: '',
      };

  const submit = async (values: SupplierFormValues) => {
    if (mode === 'edit') {
      await edit.mutateAsync({
        displayName:         values.displayName,
        description:         values.description ?? null,
        primaryContactEmail: values.primaryContactEmail,
      });
    } else {
      await create.mutateAsync({
        code:                values.code,
        displayName:         values.displayName,
        description:         values.description ?? null,
        primaryContactEmail: values.primaryContactEmail,
      });
    }
  };

  return (
    <EntityFormDialog<SupplierFormValues>
      open={open}
      mode={mode}
      title={mode === 'create' ? 'Add machine vendor' : 'Edit machine vendor'}
      schema={schema}
      initialValues={initial}
      onSubmit={submit}
      onClose={onClose}
    >
      <Fields mode={mode} />
    </EntityFormDialog>
  );
}

function Fields({ mode }: { mode: 'create' | 'edit' }) {
  const { register, formState: { errors } } = useFormContext<SupplierFormValues>();
  return (
    <>
      <TextField
        label="Code"
        size="small"
        disabled={mode === 'edit'}
        helperText={mode === 'edit'
          ? 'Code is immutable.'
          : (errors.code?.message ?? 'Letters, digits, _ and - only.')}
        error={!!errors.code}
        fullWidth
        autoFocus={mode === 'create'}
        {...register('code')}
      />
      <TextField
        label="Display name"
        size="small"
        helperText={errors.displayName?.message}
        error={!!errors.displayName}
        fullWidth
        {...register('displayName')}
      />
      <TextField
        label="Description (optional)"
        size="small"
        multiline minRows={2}
        helperText={errors.description?.message}
        error={!!errors.description}
        fullWidth
        {...register('description')}
      />
      <TextField
        label="Primary contact email"
        size="small"
        helperText={errors.primaryContactEmail?.message ?? 'Used for fleet alerts and invoicing.'}
        error={!!errors.primaryContactEmail}
        fullWidth
        {...register('primaryContactEmail')}
      />
    </>
  );
}
